import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  TOPIC_PRODUCT_LABELS,
  TOPIC_PRODUCTS,
  type Folder,
  type FolderDraft,
  type Tag,
  type TopicProduct,
  type TopicRecord,
} from '@headline/schemas'
import { type QueryClient, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { FolderPlus, Plus } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { EmptyNote, FolderFormDialog, FolderSection } from '@/components/topics/folders'
import { CopyTopicDialog } from '@/components/topics/CopyTopicDialog'
import { DeleteConfirmDialogs } from '@/components/topics/DeleteConfirmDialogs'
import { InheritedTopicsSection } from '@/components/topics/InheritedTopicsSection'
import { DraggableTopic, SharedResourcesCard } from '@/components/topics/topic-cards'
import { FilterPills } from '@/components/FilterPills'
import { PageHeader } from '@/components/PageHeader'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { accountQuery, foldersQuery, subaccountsQuery, tagsQuery, topicsQuery } from '@/lib/queries'
import { useCanEditAccount } from '@/lib/use-account-permissions'
import { useResourceLibrary } from '@/lib/use-resource-library'
import { useTopicStore } from '@/lib/topic-store'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/app/accounts/$accountId_/topics/$subaccountId/')({
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(topicsQuery(params.subaccountId)),
      context.queryClient.ensureQueryData(foldersQuery(params.subaccountId)),
      context.queryClient.ensureQueryData(tagsQuery(params.subaccountId)),
      // Needed for the copy affordances: the account's other sub-accounts (copy targets) and its
      // roll-up preference (whether inherited topics surface here).
      context.queryClient.ensureQueryData(accountQuery(params.accountId)),
      context.queryClient.ensureQueryData(subaccountsQuery(params.accountId)),
    ]),
  component: TopicsList,
})

// Stable fallback for the form dialog while it's closed. Each *open* passes a fresh draft so the
// fields reseed (a shared reference wouldn't reset between opens).
const EMPTY_FOLDER_DRAFT: FolderDraft = { name: '', description: '' }
const UNGROUPED_KEY = '__ungrouped__'

type FolderForm =
  | { mode: 'create'; initial: FolderDraft; assignTopicId?: string }
  | { mode: 'edit'; id: string; initial: FolderDraft }

/** The sub-accounts a topic can be copied to, as the copy dialog lists them. */
const copyCandidates = (subs: { id: string; name: string; isPrimary: boolean }[]) =>
  subs.map((sub) => ({
    id: sub.id,
    name: sub.name,
    hint: sub.isPrimary ? 'Main account' : undefined,
  }))

/** The form that edits an existing folder, seeded with what it says now. */
const editFolderForm = (folder: Folder): FolderForm => ({
  mode: 'edit',
  id: folder.id,
  initial: { name: folder.name, description: folder.description },
})

// The list orchestrates folders + topics + drag-and-drop + two confirm dialogs; the rows, folder
// sections, dialogs, header and drag board are their own components. What's left is the delete
// and folder-form state that splitting further would only prop-drill. Pre-existing — the account-nesting change
// only threaded an accountId prop through for the links.
// react-doctor-disable-next-line react-doctor/no-high-complexity-react-function
function TopicsList() {
  const { accountId, subaccountId } = Route.useParams()
  const queryClient = useQueryClient()
  const {
    topics: allTopics,
    folders,
    deleteTopic,
    cloneTopic,
    moveTopic,
    addFolder,
    updateFolder,
    deleteFolder,
  } = useTopicStore(subaccountId)
  const { tags: libraryTags } = useResourceLibrary(subaccountId)
  // Copying a topic OUT to another sub-account is an account-level edit; the affordance shows only
  // for an editor with somewhere to copy to. `copyTarget` names the topic the dialog is copying.
  const { data: subsData } = useSuspenseQuery(subaccountsQuery(accountId))
  const otherSubs = subsData.subaccounts.filter((sub) => sub.id !== subaccountId)
  const canCopy = useCanEditAccount(accountId)
  const canCopyTo = canCopy && otherSubs.length > 0
  const [copyTarget, setCopyTarget] = useState<TopicRecord | null>(null)
  const [pendingDelete, setPendingDelete] = useState<TopicRecord | null>(null)
  const [folderForm, setFolderForm] = useState<FolderForm | null>(null)
  const [pendingFolderDelete, setPendingFolderDelete] = useState<Folder | null>(null)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  // Where each topic is used (CLI-744) is badged on its card; the filter narrows to one product.
  const [productFilter, setProductFilter] = useState<'all' | TopicProduct>('all')
  const anyUsed = allTopics.some((topic) => topic.products.length > 0)
  const topics =
    productFilter === 'all'
      ? allTopics
      : allTopics.filter((topic) => topic.products.includes(productFilter))

  function toggleCollapsed(key: string) {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const dndEnabled = folders.length > 0

  const renderTopics = (list: TopicRecord[]) => (
    <TopicCards
      list={list}
      draggable={dndEnabled}
      accountId={accountId}
      subaccountId={subaccountId}
      libraryTags={libraryTags}
      folders={folders}
      onClone={(...args) => void cloneTopic(...args)}
      onMove={moveTopic}
      onNewFolder={(topicId) =>
        setFolderForm({
          mode: 'create',
          initial: { name: '', description: '' },
          assignTopicId: topicId,
        })
      }
      onDelete={setPendingDelete}
      onCopyTo={canCopyTo ? setCopyTarget : undefined}
    />
  )

  return (
    <div className="mx-auto flex w-full max-w-4xl animate-fade-in flex-col gap-6">
      <TopicsPageHeader
        accountId={accountId}
        subaccountId={subaccountId}
        topicCount={topics.length}
        folderCount={folders.length}
        onNewFolder={() =>
          setFolderForm({ mode: 'create', initial: { name: '', description: '' } })
        }
        anyUsed={anyUsed}
        productFilter={productFilter}
        onProductFilter={setProductFilter}
      />

      {topics.length === 0 && folders.length === 0 ? (
        <NoTopicsCard accountId={accountId} subaccountId={subaccountId} />
      ) : (
        <TopicsBoard
          topics={topics}
          folders={folders}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
          moveTopic={moveTopic}
          renderTopics={renderTopics}
          onEditFolder={(folder) => setFolderForm(editFolderForm(folder))}
          onDeleteFolder={setPendingFolderDelete}
        />
      )}

      <TopicsPageFooter
        accountId={accountId}
        subaccountId={subaccountId}
        canCopy={canCopy}
        queryClient={queryClient}
      />

      {copyTarget && (
        <CopyTopicDialog
          topic={copyTarget}
          subaccountId={subaccountId}
          candidates={copyCandidates(otherSubs)}
          onClose={() => setCopyTarget(null)}
          queryClient={queryClient}
        />
      )}

      <FolderFormHost
        folderForm={folderForm}
        onClose={() => setFolderForm(null)}
        store={{ addFolder, updateFolder, moveTopic }}
      />

      <DeleteConfirmHost
        pendingTopic={pendingDelete}
        onTopicDone={() => setPendingDelete(null)}
        pendingFolder={pendingFolderDelete}
        onFolderDone={() => setPendingFolderDelete(null)}
        store={{ deleteTopic, deleteFolder }}
      />
    </div>
  )
}

interface TopicsPageHeaderProps {
  accountId: string
  subaccountId: string
  topicCount: number
  folderCount: number
  onNewFolder: () => void
  anyUsed: boolean
  productFilter: 'all' | TopicProduct
  onProductFilter: (value: 'all' | TopicProduct) => void
}

/** The title, the two create actions, and — once any topic is used somewhere — the product filter. */
function TopicsPageHeader({
  accountId,
  subaccountId,
  topicCount,
  folderCount,
  onNewFolder,
  anyUsed,
  productFilter,
  onProductFilter,
}: TopicsPageHeaderProps) {
  return (
    <>
      <PageHeader
        title="Topics"
        description={`${topicCount} ${topicCount === 1 ? 'topic' : 'topics'} · ${folderCount} ${folderCount === 1 ? 'folder' : 'folders'}`}
        className="mb-0"
        actions={
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={onNewFolder}>
              <FolderPlus className="size-4" /> New folder
            </Button>
            <Link
              to="/app/accounts/$accountId/topics/$subaccountId/new"
              params={{ accountId, subaccountId }}
              className={cn(buttonVariants())}
            >
              <Plus className="size-4" /> New topic
            </Link>
          </div>
        }
      />

      {anyUsed && (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Used for">
          <FilterPills
            options={[
              { value: 'all', label: 'All topics' },
              ...TOPIC_PRODUCTS.map((product) => ({
                value: product,
                label: TOPIC_PRODUCT_LABELS[product],
              })),
            ]}
            value={productFilter}
            onChange={onProductFilter}
          />
        </div>
      )}
    </>
  )
}

function NoTopicsCard({ accountId, subaccountId }: { accountId: string; subaccountId: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
        <p className="text-sm text-muted-foreground">
          No topics yet. Create one to start collecting results for this client.
        </p>
        <Link
          to="/app/accounts/$accountId/topics/$subaccountId/new"
          params={{ accountId, subaccountId }}
          className={cn(buttonVariants())}
        >
          <Plus className="size-4" /> New topic
        </Link>
      </CardContent>
    </Card>
  )
}

interface TopicsBoardProps {
  topics: TopicRecord[]
  folders: Folder[]
  /** Owned by the page, so a folder stays collapsed across the board unmounting (an empty filter). */
  collapsed: Set<string>
  onToggleCollapsed: (key: string) => void
  moveTopic: (topicId: string, folderId: string | null) => void
  renderTopics: (list: TopicRecord[]) => ReactNode
  onEditFolder: (folder: Folder) => void
  onDeleteFolder: (folder: Folder) => void
}

/** The folders and the Ungrouped bucket, with drag-and-drop between them. */
function TopicsBoard({
  topics,
  folders,
  collapsed,
  onToggleCollapsed,
  moveTopic,
  renderTopics,
  onEditFolder,
  onDeleteFolder,
}: TopicsBoardProps) {
  const [activeId, setActiveId] = useState<string | null>(null)
  // A small drag threshold keeps clicks on the card's buttons from starting a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  // A topic is ungrouped if it has no folder, or points at a folder that no longer exists.
  const inFolder = (folderId: string) => topics.filter((topic) => topic.folderId === folderId)
  const ungrouped = topics.filter(
    (topic) => topic.folderId === null || !folders.some((folder) => folder.id === topic.folderId),
  )
  const dndEnabled = folders.length > 0
  const activeTopic = activeId
    ? topics.find((topic) => `topic:${topic.id}` === activeId)
    : undefined

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id))
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null)
    const topicId = event.active.data.current?.topicId as string | undefined
    if (!topicId || !event.over) return
    // `folderId` is null for the Ungrouped bucket — a valid destination, not a no-op.
    const destination = (event.over.data.current?.folderId ?? null) as string | null
    moveTopic(topicId, destination)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveId(null)}
    >
      {!dndEnabled ? (
        // No folders yet — a plain flat list of every topic (nowhere to drag into).
        renderTopics(topics)
      ) : (
        <div className="flex flex-col gap-4">
          {folders.map((folder) => {
            const folderTopics = inFolder(folder.id)
            return (
              <FolderSection
                key={folder.id}
                folder={folder}
                topicCount={folderTopics.length}
                collapsed={collapsed.has(folder.id)}
                onToggleCollapsed={() => onToggleCollapsed(folder.id)}
                onEdit={() => onEditFolder(folder)}
                onDelete={() => onDeleteFolder(folder)}
              >
                {folderTopics.length > 0 ? (
                  renderTopics(folderTopics)
                ) : (
                  <EmptyNote>Empty. Drag a topic here, or use a topic’s folder menu.</EmptyNote>
                )}
              </FolderSection>
            )
          })}

          {/* Always present so a topic can be dragged out of a folder. */}
          <FolderSection
            folder={null}
            topicCount={ungrouped.length}
            collapsed={collapsed.has(UNGROUPED_KEY)}
            onToggleCollapsed={() => onToggleCollapsed(UNGROUPED_KEY)}
          >
            {ungrouped.length > 0 ? (
              renderTopics(ungrouped)
            ) : (
              <EmptyNote>No loose topics — everything is filed into a folder.</EmptyNote>
            )}
          </FolderSection>
        </div>
      )}

      <DragOverlay>
        {activeTopic ? (
          <div className="pointer-events-none rounded-lg border border-primary bg-card px-3 py-2 text-sm font-medium shadow-lg">
            {activeTopic.name || 'Untitled'}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

/** The quieter material under the list: copyable topics from elsewhere, then shared resources. */
function TopicsPageFooter({
  accountId,
  subaccountId,
  canCopy,
  queryClient,
}: {
  accountId: string
  subaccountId: string
  canCopy: boolean
  queryClient: QueryClient
}) {
  return (
    <>
      {/* Topics from the account's other sub-accounts, copyable into this one — only when rolled up. */}
      <InheritedTopicsSection
        accountId={accountId}
        subaccountId={subaccountId}
        canCopy={canCopy}
        queryClient={queryClient}
      />

      {/* A supporting concern, not a starting point — kept quiet at the foot of the list. */}
      <div className="mt-2 flex flex-col gap-2 border-t border-border pt-6">
        <span className="text-xs font-medium text-muted-foreground uppercase">
          Shared resources
        </span>
        <SharedResourcesCard accountId={accountId} subaccountId={subaccountId} />
      </div>
    </>
  )
}

type TopicStore = ReturnType<typeof useTopicStore>

/** The folder create/edit dialog, and what its submit does for each mode. */
function FolderFormHost({
  folderForm,
  onClose,
  store,
}: {
  folderForm: FolderForm | null
  onClose: () => void
  store: Pick<TopicStore, 'addFolder' | 'updateFolder' | 'moveTopic'>
}) {
  async function submitFolderForm(draft: FolderDraft) {
    if (!folderForm) return
    if (folderForm.mode === 'edit') {
      store.updateFolder(folderForm.id, draft)
    } else {
      const folder = await store.addFolder(draft)
      // Created from a topic's menu — file that topic into the new folder in one step.
      if (folderForm.assignTopicId) store.moveTopic(folderForm.assignTopicId, folder.id)
    }
    onClose()
  }

  return (
    <FolderFormDialog
      open={folderForm !== null}
      mode={folderForm?.mode ?? 'create'}
      initial={folderForm?.initial ?? EMPTY_FOLDER_DRAFT}
      onClose={onClose}
      onSubmit={(draft) => void submitFolderForm(draft)}
    />
  )
}

interface TopicCardsProps {
  list: TopicRecord[]
  draggable: boolean
  accountId: string
  subaccountId: string
  libraryTags: Tag[]
  folders: Folder[]
  onClone: (topicId: string) => void
  onMove: (topicId: string, folderId: string | null) => void
  onNewFolder: (topicId: string) => void
  onDelete: (topic: TopicRecord) => void
  onCopyTo: ((topic: TopicRecord) => void) | undefined
}

function TopicCards({
  list,
  onClone,
  onMove,
  onNewFolder,
  onDelete,
  onCopyTo,
  ...card
}: TopicCardsProps) {
  return (
    <ul className="flex flex-col gap-3">
      {list.map((topic) => (
        <li key={topic.id}>
          <DraggableTopic
            {...card}
            topic={topic}
            onClone={() => onClone(topic.id)}
            onMove={(folderId) => onMove(topic.id, folderId)}
            onNewFolder={() => onNewFolder(topic.id)}
            onDelete={() => onDelete(topic)}
            onCopyTo={onCopyTo ? () => onCopyTo(topic) : undefined}
          />
        </li>
      ))}
    </ul>
  )
}

/** The topic and folder delete confirmations; a confirm deletes, then either way the prompt closes. */
function DeleteConfirmHost({
  pendingTopic,
  onTopicDone,
  pendingFolder,
  onFolderDone,
  store,
}: {
  pendingTopic: TopicRecord | null
  onTopicDone: () => void
  pendingFolder: Folder | null
  onFolderDone: () => void
  store: Pick<TopicStore, 'deleteTopic' | 'deleteFolder'>
}) {
  return (
    <DeleteConfirmDialogs
      pendingTopic={pendingTopic}
      onCancelTopic={onTopicDone}
      onConfirmTopic={() => {
        if (pendingTopic) store.deleteTopic(pendingTopic.id)
        onTopicDone()
      }}
      pendingFolder={pendingFolder}
      onCancelFolder={onFolderDone}
      onConfirmFolder={() => {
        if (pendingFolder) store.deleteFolder(pendingFolder.id)
        onFolderDone()
      }}
    />
  )
}
